<script setup>
import {
  ref,
  computed,
  reactive,
  defineProps,
  watchEffect,
  watch,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { RouterLink } from 'vue-router';
import { isMobile } from 'mobile-device-detect';
import useMqttControl from '@/common/hooks/useMqttControl';
import useModal from '@/common/hooks/useModal';
import ZoneSelectModal from './ZoneSelectModal.vue';

const mqttControl = reactive(useMqttControl());
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = computed(() => store.getters);
const { emitter, mobile, mqtt } =
  getCurrentInstance().appContext.config.globalProperties;

const mqttSubscribed = computed(() => mqtt.isSubscribed());
const mqttConnected = computed(() => mqtt.isConnected());

watch(mqttConnected, async () => {
  if (mqttConnected.value == false) {
    // let intervalCount = 0;
    // const intervalId = setInterval(async () => {
    //   if (mqttConnected.value || intervalCount >= 20) {
    //     clearInterval(intervalId);
    //     return;
    //   }

    //   mqttControl.connectAuth();
    //   console.log("try connect auth...")
    //   intervalCount++;
    // }, 5000);
  }
})

const currentZone = computed({
  get: () => {
    return store.getters['home/getCurrent'].zoneId;
  },

  set: (value) => {
    store.commit('home/setCurrentZone', value);
    store.dispatch('home/saveSelectedZone', { id: value });
  },
});

const zones = computed(() => {
  return store.getters['home/getZones'];
});

const getCurrentZone = computed(() => {
  return store.getters['home/getCurrentZone'];
});

const getCurrentControllers = computed(() => {
  return getCurrentZone.value?.controllers || [];
});

const isHome = computed(() => {
  return route.path === '/rooms' || route.path === '/rooms/';
});

// 사이드 메뉴 URL 목록 (1depth)
const sidebarMenuUrls = ['/rooms', '/notices', '/settings', '/profile', '/share', '/update'];

// 현재 경로가 사이드 메뉴 1depth인지 확인
const isSidebarMenu1Depth = computed(() => {
  const currentPath = route.path;
  return sidebarMenuUrls.some(url => {
    // 정확히 일치하거나, 해당 경로로 시작하면서 다음 문자가 '/'이거나 없는 경우
    return currentPath === url || currentPath === url + '/' || 
           (currentPath.startsWith(url + '/') && currentPath !== url + '/');
  });
});

// 현재 경로가 사이드 메뉴 1depth 섹션인지 확인 (정확히 일치)
const isSidebarMenu1DepthExact = computed(() => {
  const currentPath = route.path;
  return sidebarMenuUrls.some(url => currentPath === url || currentPath === url + '/');
});

// 현재 경로가 사이드 메뉴 2depth+인지 확인
const isSidebarMenu2DepthPlus = computed(() => {
  const currentPath = route.path;
  return sidebarMenuUrls.some(url => {
    // 해당 경로로 시작하지만 정확히 일치하지 않는 경우 (2depth+)
    return currentPath.startsWith(url + '/') && currentPath !== url && currentPath !== url + '/';
  });
});

// PC 환경인지 확인
const isPC = computed(() => !isMobile);

// 백 버튼 표시 여부
const showBackButton = computed(() => {
  // PC 환경에서는 백 버튼 숨김
  if (isPC.value) return false;
  // 홈에서는 백 버튼 숨김
  if (isHome.value) return false;
  // 사이드 메뉴 1depth 섹션에서는 백 버튼 숨김
  if (isSidebarMenu1DepthExact.value) return false;
  // 그 외의 경우 (2depth+) 백 버튼 표시
  return true;
});

// 메뉴 버튼 표시 여부
const showMenuButton = computed(() => {
  // PC 환경에서는 메뉴 버튼 숨김 (기존 로직 유지)
  if (isPC.value) return false;
  // 사이드 메뉴 정확히 1depth 섹션에서는 항상 메뉴 아이콘 표시
  if (isSidebarMenu1DepthExact.value) return true;
  // 그 외의 경우는 isShowMenu.value 값에 따라 결정
  return isShowMenu.value;
});

const isOwner = computed(() => {
  return getCurrentZone.value?.isOwner || false;
});

const hasMultipleZones = computed(() => {
  return zones.value && zones.value.length > 1;
});

const title = ref('');
const isShowZone = ref(true);
const isShowMenu = ref(true);
const isShowProfile = ref(true);
const isShowRightHome = ref(false);
const zoneSelectModal = reactive(useModal());


let topics = [];

onMounted(async () => {
  await subscribe();
  emitter.on('onChangeHeaderSetting', (params) => {
    // console.log('Header.onMounted => onChangeHeaderSetting : ', params);
    if (params.title != undefined) title.value = params.title;
    if (params.isShowZone != undefined) isShowZone.value = params.isShowZone;
    if (params.isShowMenu != undefined) isShowMenu.value = params.isShowMenu;
    if (params.isShowProfile != undefined)
      isShowProfile.value = params.isShowProfile;
    if (params.isShowRightHome != undefined)
      isShowRightHome.value = params.isShowRightHome;
  });
  console.log('Header.onMounted => mqtt isConnected : ', mqttControl.isConnected());  

  // mqttControl.registerEvent('on-connect', async(topic,message) => {
  //   console.log("mqtt event: on-connect");
  //   await unSubscribe(topics)    
  //   await subscribe()
  // })
});

onBeforeUnmount(() => {
  console.log('Header.onBeforeUnmount');
  mqttControl.unRegisterEvent('on-connect');
  mqttControl.unRegisterEvent('on-disconnect');
  mqttControl.unRegisterEvent('on-reconnect');
  mqttControl.unRegisterEvent('on-connect-fail');
});

watch(mqttConnected, async () => {
  if (mqttConnected.value) {
    await unSubscribe(topics);
    await subscribe();
  }
});

watch(currentZone, async () => {

  console.log('Header.watch - currentZone');
  await subscribe();

  if (route.path.includes('/rooms/')) {
    // 영역이 변경된 경우 home으로 이동함. (히스토리 초기화)
    router.replace('/rooms').then(() => {
      // PC 브라우저에서도 확실하게 히스토리 초기화
      if (window.history.replaceState) {
        window.history.replaceState(null, '', '/rooms');
      }
    });
  } else if (route.path.includes('/devices/')) {
    // 영역이 변경된 경우 home으로 이동함. (히스토리 초기화)
    router.replace('/devices').then(() => {
      if (window.history.replaceState) {
        window.history.replaceState(null, '', '/devices');
      }
    });
  } else if (route.path.includes('/schedules/')) {
    // 영역이 변경된 경우 home으로 이동함. (히스토리 초기화)
    router.replace('/schedules').then(() => {
      if (window.history.replaceState) {
        window.history.replaceState(null, '', '/schedules');
      }
    });
  }
});

watch(getCurrentControllers, async () => {
  console.log('Header.watch - getCurrentControllers');  
  await subscribe();
});

watch(zones, async (newZones) => {
  if (newZones?.length > 0) {
    await subscribe();
  }
});

const getTopics = (() => {
  const tempTopics = []
  zones.value.forEach((z) => {
    tempTopics.push(
        '$' +
          process.env.VUE_APP_MQTT_DEFAULT_TOPIC_PREFIX +
          '/' +
          process.env.VUE_APP_MQTT_ZONE_TOPIC_PREFIX +
          `/${z._id}/${process.env.VUE_APP_MQTT_PUBLISH_TOPIC_SIRFIX}`,
      );
    });

  zones.value.forEach((z) => {
    let controllers = z.controllers;

    if (controllers && controllers.length > 0) {
      controllers.forEach((c) => {
      tempTopics.push(
          '$' +
            process.env.VUE_APP_MQTT_DEFAULT_TOPIC_PREFIX +
            '/' +
            process.env.VUE_APP_MQTT_MCS_TOPIC_PREFIX +
            `/${c._id}/${process.env.VUE_APP_MQTT_SUBSCRIBE_TOPIC_SIRFIX}`,
        );
      });
    }
  });

  zones.value.forEach((z) => {
    z.groups?.forEach((group) => {
      const boilerDevices = group.devices.filter(device => device.type == 2);
      boilerDevices.forEach(device => tempTopics.push(`stat/inshow/${device.controllerId}/#`));
    });
  });  

  if(process.env.VUE_APP_ENABLE_TEMP_CONTROLLER){
    // tempTopics.push(process.env.VUE_APP_MQTT_TEMP_TOPIC_PREFIX+'/#');
    // tempTopics.push(process.env.VUE_APP_MQTT_TEMP_TOPIC_PREFIX_STATE+'/#');
  }
  return tempTopics;
})

const subscribe = async () => {
  // console.log("Header.subscribe => request subscribe~");
    
  const newTopics = getTopics();
  
  if(newTopics.length == 0) return;

  const difference = newTopics?.filter((element) => !topics.includes(element));
  if(newTopics.length != topics.length || difference?.length > 0){    
    if(topics.length > 0)await unSubscribe();    

    // console.log("Header.subscribe => newTopics => ",newTopics);
    // console.log("Header.subscribe => topics => ",topics);
    // console.log("Header.subscribe => subscribe diff => ",difference);

    //topics = newTopics

    topics = [...newTopics] // 보일러용 mqtt topic 기본 추가
    return mqttControl.subscribe(topics);
  }

  if (mqttControl.isConnected()) {
    mqttControl.resyncBoilerModes();
  }
};

const unSubscribe = async () => {
  if(topics.length == 0) return;
  await mqttControl.unSubscribe(topics);  
  topics = []
  return
};

function toggleAppSidebarMinify() {
  store.commit('setAppSidebarMinified', !getters.value.appSidebarMinified);
}
function toggleAppSidebarMobileToggled() {
  store.commit(
    'setAppSidebarMobileToggled',
    !getters.value.appSidebarMobileToggled,
  );
}
function toggleAppHeaderSearch(event) {
  event.preventDefault();

  store.commit(
    'setAppHeaderSearchToggled',
    !getters.value.appHeaderSearchToggled,
  );
}
function checkForm(event) {
  event.preventDefault();
  this.$router.push({ path: '/extra/search' });
}

function onBackClick() {
  // 기본 브라우저 내비게이션 방식 사용
  router.go(-1);
}

function onRightHomeClick() {
  router.replace('/rooms');
}

const onRefreshClick = async () => {
  emitter.emit('showLoading');
  getCurrentZone.value.controllers.forEach(controller => {
    mqttControl.onPublishController(controller._id, {
      serial: controller._id,
      cmd: "q",
      type: 1
    });
  });
  setTimeout(() => emitter.emit('hideLoading'), 1000);
}

const onZoneSelectClick = () => {
  if (hasMultipleZones.value) {
    zoneSelectModal.show();
  }
};

const onZoneSelect = (zoneId) => {
  currentZone.value = zoneId;
  zoneSelectModal.hide();
};


// const onTest = async () => {
//   console.log("on test !!!!!!!!!!!!!!!!!!!!!")
//   console.log(mqttControl.isConnected())
//   console.log(mqttConnected.value)

//   if (mqttControl.isConnected()) {
//     mqtt.disconnect();
//   } else {
//     mqtt.connect();
//   }
// }
</script>

<template>
  <div id="header" class="app-header">
    <!-- BEGIN mobile-toggler -->
    <div class="mobile-toggler" v-if="showBackButton">
      <button type="button" class="menu-toggler" @click="onBackClick">
        <i class="fa-solid fa-arrow-left header-nav-icon"></i>
      </button>
    </div>
    <div class="mobile-toggler" v-if="showMenuButton">
      <button
        type="button"
        class="menu-toggler"
        v-on:click="toggleAppSidebarMobileToggled"
      >
        <i class="fa-solid fa-bars-staggered header-nav-icon"></i>
      </button>
    </div>
    <!-- END mobile-toggler -->
    <!-- getCurrentZone:{{getCurrentZone._id}} -->
    <!-- BEGIN brand -->
    <div class="brand" v-if="!isShowZone || !zones || zones.length === 0">
      <div class="desktop-toggler" style="width: 40px" v-if="showBackButton && isPC">
        <button
          type="button"
          class="menu-toggler"
          style="padding-right: 0"
          @click="onBackClick"
        >
          <i class="fa-solid fa-arrow-left header-nav-icon"></i>
        </button>
      </div>

      <div class="desktop-toggler">
        <button
          type="button"
          class="menu-toggler"
          v-on:click="toggleAppSidebarMinify"
        >
          <i class="fa-solid fa-bars-staggered header-nav-icon"></i>
        </button>
      </div>

      <a class="brand-logo">
        <img
          :src="require('@/assets/images/inshow_logo.png')"
          alt=""
          height="20"
          :style="`padding-left: ${isHome ? 0 : 0}rem`"
        />
      </a>
    </div>
    <!-- END brand -->

    <!-- BEGIN menu -->
    <div class="menu">
      <form
        class="menu-search"
        name="header_search_form"
        v-on:submit="checkForm"
      >
        <div v-if="title">
          <h4 style="margin-top: 9px">{{ title }}</h4>
        </div>
        <div 
          v-if="isShowZone && zones && zones.length > 0" 
          class="zone-display-area"
          :class="{ 'zone-clickable': hasMultipleZones }"
          @click="hasMultipleZones ? onZoneSelectClick() : null"
          :style="hasMultipleZones ? 'cursor: pointer;' : ''"
        >
          <div class="menu-search-icon">
            <i
              :class="isOwner ? 'fa-regular fa-house header-action-icon' : 'fa-solid fa-house-user header-action-icon'"
            ></i>
          </div>

          <!-- Zone 이름 표시 -->
          <div class="zone-name-display">
            <span style="font-size: 1rem; color: var(--app-theme); font-weight: 500;">
              {{ getCurrentZone?.name }}{{ getCurrentZone?.isOwner ? '' : ' (Share)' }}
            </span>
          </div>
        </div>
      </form>
      <div v-if="isShowRightHome" class="menu-item">
        <a class="menu-link" href="#" @click.prevent="onRightHomeClick">
          <div class="menu-img">
            <i class="fa-regular fa-house header-action-icon"></i>
          </div>
        </a>
      </div>
      <div class="menu-item" v-if="isShowProfile">
        <a class="menu-link px-2" @click.prevent="onRefreshClick" href="#" role="button">
          <div class="menu-img">
            <i class="fa-solid fa-arrow-rotate-right header-action-icon header-action-icon--refresh"></i>
          </div>
        </a>
      </div>
      <div class="menu-item dropdown" v-if="isShowProfile">
        <a data-bs-toggle="dropdown" data-display="static" class="menu-link px-2">
          <div class="menu-img">
            <i class="fa-regular fa-user header-action-icon"></i>
          <span v-if="!mqttConnected" class="position-absolute translate-middle badge rounded-pill bg-danger" style="top: 10%; left: 90%; font-size: 10px; min-width: 16px; height: 16px; padding: 2px 4px;">!</span>
          <span v-else-if="!mqttSubscribed" class="position-absolute translate-middle badge rounded-pill bg-warning" style="top: 10%; left: 90%; font-size: 10px; min-width: 16px; height: 16px; padding: 2px 4px;">!</span>
          </div>
          <div class="menu-text">{{ getters.account?.nickName }}</div>
        </a>
        <div
          class="dropdown-menu dropdown-menu-end me-lg-3"
          @click.prevent="() => {}"
        >
          <RouterLink
            to="/profile"
            class="dropdown-item d-flex align-items-center"
            >프로필
            <i class="fa-regular fa-user fa-fw ms-auto text-gray-400 header-dropdown-icon"></i
          ></RouterLink>
          <RouterLink
            to="/share"
            class="dropdown-item d-flex align-items-center"
            >공유
            <i
              class="fa-regular fa-share-from-square fa-fw ms-auto text-gray-400 header-dropdown-icon"
            ></i
          ></RouterLink>
          <!-- <div class="dropdown-divider"></div>
          <div
            class="dropdown-item d-flex align-items-center"
            @click="onRefreshClick"
          >
            새로고침
            <i class="fa fa-arrows-rotate fa-fw ms-auto text-gray-400 fs-16px"></i>
          </div> -->
        </div>
      </div>
    </div>
    <!-- END menu -->
  </div>

  <!-- Zone 선택 다이얼로그 -->
  <ZoneSelectModal
    :visible="zoneSelectModal.visible"
    :zones="zones"
    :currentZoneId="currentZone"
    :onSelect="onZoneSelect"
    @close="zoneSelectModal.hide"
  />
</template>

<style scoped>

.header-nav-icon {
  font-size: 1.25rem;
  line-height: 1;
}

.header-action-icon {
  font-size: 1.25rem;
  line-height: 1;
  color: var(--app-theme);
}

.header-action-icon--refresh {
  transform: scale(0.9);
}

.header-dropdown-icon {
  font-size: 1rem;
}

.zone-display-area {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  flex: 1;
  min-width: 0;
}

.zone-display-area .menu-search-icon {
  position: static !important;
  width: auto !important;
  height: auto !important;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zone-name-display {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.zone-clickable {
  transition: opacity 0.2s ease;
}

.zone-clickable:hover {
  opacity: 0.8;
}

.zone-clickable:active {
  opacity: 0.6;
}
</style>
